import { ReactNode, useMemo } from 'react';
import { css } from '@emotion/react';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { callIntelKpiUtils } from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Text, useTooltip } from '@frontend/design-system';
import { usePreviousTimePeriodLabel } from '../../../hooks/use-previous-time-period-label';
import { useActiveFilters } from './use-active-filters';

type ServiceIssueInsightProps = {
  stats?: CallIntelTypes.ServiceQualityStats;
};

type InsightCounts = {
  currentAnalyzedCalls: number;
  currentServiceIssueCalls: number;
  previousAnalyzedCalls: number;
  previousServiceIssueCalls: number;
};

type ServiceIssueRates = {
  current: number;
  previous?: number;
};

type ServiceIssueTrend = {
  change: CallIntelTypes.ChangeType;
  percentageChange?: number;
};

type PartialIntervals = {
  isCurrentPartial: boolean;
  isPreviousPartial: boolean;
};

type ServiceIssueInsight = {
  counts: InsightCounts;
  rates: ServiceIssueRates;
  subTitle: ReactNode;
  title: ReactNode;
  trend: ServiceIssueTrend;
  isPartial: PartialIntervals;
  muted: boolean;
};

const Title = ({ message, muted, partialNote }: { message: string; muted?: boolean; partialNote?: string }) => {
  const { Tooltip: Tooltip, triggerProps, tooltipProps } = useTooltip({ placement: 'top' });

  return (
    <>
      <Text css={styles.titleText(muted)} size='medium' weight='bold' {...triggerProps}>
        {message}
      </Text>
      {partialNote && (
        <Tooltip {...tooltipProps}>
          <Text size='medium' css={styles.tooltipText}>
            {partialNote}
          </Text>
        </Tooltip>
      )}
    </>
  );
};

const RateWithCalls = ({ calls, rate }: { calls: number; rate?: number }) => {
  const { t } = useTranslation('analytics');
  return (
    <span css={styles.rateGroup}>
      {rate !== undefined && rate > 0 && (
        <Text size='small' weight='bold' css={styles.secondaryText}>
          {callIntelKpiUtils.formatValue(rate, 'percent')}
        </Text>
      )}
      <Text size='small' css={[styles.secondaryText, styles.noWrap]}>
        {t('({{count}} Calls)', { count: calls })}
      </Text>
    </span>
  );
};

const SubTitle = ({
  currentRate,
  currentServiceIssueCalls,
  previousRate,
  previousServiceIssueCalls,
}: {
  currentRate?: number;
  currentServiceIssueCalls: number;
  previousRate?: number;
  previousServiceIssueCalls: number;
}) => {
  const { t } = useTranslation('analytics');
  const { filterHintText: currentPeriodLabel } = useActiveFilters();
  const previousTimePeriodLabel = usePreviousTimePeriodLabel(currentPeriodLabel);

  return (
    <div css={styles.subTitleWrapper}>
      <div css={styles.subTitleRow}>
        <Text color='subdued' size='small' css={styles.noWrap}>
          {t(previousTimePeriodLabel)}
        </Text>
        <RateWithCalls calls={previousServiceIssueCalls} rate={previousRate} />
        <Text color='subdued' size='small' css={styles.secondaryText}>
          →
        </Text>
      </div>
      <div css={styles.subTitleRow}>
        <Text color='subdued' size='small' css={styles.noWrap}>
          {t(currentPeriodLabel)}
        </Text>
        <RateWithCalls calls={currentServiceIssueCalls} rate={currentRate} />
      </div>
    </div>
  );
};

export const useServiceIssueInsight = ({ stats }: ServiceIssueInsightProps): ServiceIssueInsight => {
  const { t } = useTranslation('analytics');

  return useMemo<ServiceIssueInsight>(() => {
    const currentStats = stats?.currentStats;
    const previousStats = stats?.previousStats;

    const currentAnalyzedCalls = currentStats?.analyzedCallsCount ?? 0;
    const previousAnalyzedCalls = previousStats?.analyzedCallsCount ?? 0;

    const currentServiceIssueCalls = currentStats?.callsWithSqCount ?? 0;
    const previousServiceIssueCalls = previousStats?.callsWithSqCount ?? 0;

    const currentServiceIssueRate = callIntelKpiUtils.toPercent(currentServiceIssueCalls, currentAnalyzedCalls);

    const previousServiceIssueRate =
      previousAnalyzedCalls > 0
        ? callIntelKpiUtils.toPercent(previousServiceIssueCalls, previousAnalyzedCalls)
        : undefined;

    const trend = callIntelKpiUtils.computeRelativeChange({
      currentBaseCount: currentAnalyzedCalls,
      currentPercent: currentServiceIssueRate,
      previousBaseCount: previousAnalyzedCalls,
      previousPercent: previousServiceIssueRate,
    });

    const partialNote =
      stats?.currentStats?.isPartial && stats?.previousStats?.isPartial
        ? t('*Partial comparison. Data for selected and previous period are limited.')
        : stats?.currentStats?.isPartial
        ? t('*Partial comparison. Current time period is ongoing.')
        : stats?.previousStats?.isPartial
        ? t('*Partial comparison. Limited data in previous period.')
        : undefined;

    const showAsterisk = Boolean(partialNote);

    let message = '';
    let muted = false;

    if (trend.change === 'noData') {
      // case 1 — No data
      message =
        currentAnalyzedCalls === 0
          ? t('No data for this period. Insights are unavailable.')
          : t('No data for the previous period. Insights are unavailable.');
      muted = true;
    } else if (trend.change === 'unchanged') {
      // case 2 — No change
      message = t('No change in Service Issues since the previous period.{{partial}}', {
        partial: showAsterisk ? '*' : '',
      });
    } else if (trend.change === 'increased') {
      // case 3 — Increased
      const delta = trend.percentageChange ?? 0;
      message = t('Service Issues have increased by {{delta}}%.{{partial}}', {
        delta,
        partial: showAsterisk ? '*' : '',
      });
    } else {
      // case 4 — Decreased
      const delta = trend.percentageChange ?? 0;
      message = t('Service Issues have decreased by {{delta}}%.{{partial}}', {
        delta,
        partial: showAsterisk ? '*' : '',
      });
    }

    //Final override for dual‐period no data
    if (
      previousServiceIssueCalls === 0 &&
      previousStats?.isPartial &&
      currentServiceIssueCalls === 0 &&
      currentStats?.isPartial
    ) {
      message = t('No data for this period and the previous period. Insights are unavailable.');
      muted = true;
    } else if (previousServiceIssueCalls === 0 && previousStats?.isPartial) {
      message = t('No data for the previous period. Insights are unavailable.');
      muted = true;
    }

    const counts: InsightCounts = {
      currentAnalyzedCalls,
      currentServiceIssueCalls,
      previousAnalyzedCalls,
      previousServiceIssueCalls,
    };

    const rates: ServiceIssueRates = {
      current: callIntelKpiUtils.formatPercentWithPrecision(currentServiceIssueRate),
      previous: callIntelKpiUtils.formatPercentWithPrecision(previousServiceIssueRate ?? 0),
    };

    const isPartial = {
      isCurrentPartial: !!stats?.currentStats?.isPartial,
      isPreviousPartial: !!stats?.previousStats?.isPartial,
    };

    return {
      counts,
      rates,
      subTitle: (
        <SubTitle
          currentRate={rates.current}
          currentServiceIssueCalls={currentServiceIssueCalls}
          previousRate={rates.previous}
          previousServiceIssueCalls={previousServiceIssueCalls}
        />
      ),
      title: (
        <Title message={message} partialNote={trend.change !== 'noData' ? partialNote : undefined} muted={muted} />
      ),
      trend,
      isPartial,
      muted,
    };
  }, [stats, t]);
};

const styles = {
  subTitleWrapper: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    flex-wrap: wrap;
  `,
  subTitleRow: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  titleText: (muted?: boolean) => css`
    color: ${muted ? theme.colors.neutral50 : theme.colors.neutral70};
    font-weight: ${muted ? theme.font.weight.regular : theme.font.weight.bold};
  `,
  rateGroup: css`
    display: inline-flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
  `,
  secondaryText: css`
    color: ${theme.colors.neutral70};
  `,
  tooltipText: css`
    font-style: italic;
    color: ${theme.colors.white};
  `,
  noWrap: css`
    white-space: nowrap;
  `,
};
